import { useFormikContext } from 'formik';
import { User } from 'mission-control-common-components';
import React, { useEffect } from 'react';

import type { GetCreativeByIdQuery } from '../../../apis/graphql';
import type { AdType } from '../../../configs';
import type { PermissionsNames } from '../../../constants';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import type { Nullable } from '../../../models';
import bem from '../../../utils/bem';
import { isDxcCreativeType } from '../../../utils/creative';
import { useTrackFormFieldChanges } from '../../hooks/useTrackFormFieldChanges';
import type { AdFormValues } from '../adFormik';
import { AdFormFields } from '../adFormik/fields';
import { adProductDimensionSpec } from '../CreativeBuilders/dimensionsConfig';
import useCreativeId from '../CreativeBuilders/hooks/useCreativeId';
import type { CreativesBuilderValues } from '../CreativeBuilders/types';
import { CreativeEditMode } from '../CreativeBuilders/types';
import CreativeModeSelector from '../CreativeModeSelector';
import CreativeSearcher from '../CreativeSearcher';
import type { AdsPageLineItemType } from '../types';
import { typeIsVast } from '../utils';
import { creativeBuilderByAdType, HIDE_BUILD_NEW_CREATIVE_BLOCK_AD_TYPES } from './constants';

const [block] = bem('creative');

type Props = {
  creativeData: GetCreativeByIdQuery | undefined;
  loadingCreativeData: boolean;
  adType: AdType;
  lineItemType: AdsPageLineItemType;
  lineItemCountry?: Nullable<string>;
  lineItemPublishers: string[];
  editMode: CreativeEditMode;
  isExistingAd?: boolean;
  isAdApproved?: boolean;
};

const CreativeBuilderRenderer = ({
  creativeData,
  loadingCreativeData,
  adType,
  lineItemType,
  lineItemCountry,
  lineItemPublishers,
  editMode,
  isExistingAd,
  isAdApproved,
}: Props): JSX.Element => {
  const adTypeDimensions = adProductDimensionSpec[adType];
  const isDxcCreative = creativeData?.getCreativeByIDV5 && isDxcCreativeType(creativeData.getCreativeByIDV5.type);

  const CreativeBuilder = creativeBuilderByAdType[adType];
  // Non-Vast creatives show the vast preview only if the Ad has been saved
  // Vast creatives show the vast preview always, regardless of whether the ad has been saved
  const isCreativeEditingDisabled =
    (typeIsVast(creativeData?.getCreativeByIDV5.type) ? !!creativeData : !!creativeData && isExistingAd) ||
    isDxcCreative;
  const permissions = User.getPermissions<PermissionsNames>();
  const editModePermissionCheck = !permissions?.updateCreative ? CreativeEditMode.readonly : editMode;

  return (
    <CreativeBuilder
      isPreview={isCreativeEditingDisabled}
      adTypeDimensions={adTypeDimensions}
      lineItemType={lineItemType}
      lineItemCountry={lineItemCountry}
      lineItemPublishers={lineItemPublishers}
      creativeData={creativeData}
      editMode={editModePermissionCheck}
      isAdApproved={isAdApproved}
      loadingCreativeData={loadingCreativeData}
    />
  );
};

type CardContentsByModeProps = {
  mode: CreativesBuilderValues['mode'];
  creativeData: GetCreativeByIdQuery | undefined;
  loadingCreativeData: boolean;
  type: AdType;
  lineItemType: AdsPageLineItemType;
  editMode: CreativeEditMode;
  isExistingAd?: boolean;
  isAdApproved?: boolean;
  lineItemCountry?: Nullable<string>;
  lineItemPublishers: string[];
};

const cardContentsByMode = ({
  mode,
  creativeData,
  loadingCreativeData,
  type,
  lineItemType,
  editMode,
  isAdApproved,
  isExistingAd,
  lineItemCountry,
  lineItemPublishers,
}: CardContentsByModeProps): React.JSX.Element => {
  const hideBuildBlock = HIDE_BUILD_NEW_CREATIVE_BLOCK_AD_TYPES.includes(type);

  switch (mode) {
    case 'selectOption':
      return <CreativeModeSelector hideBuildBlock={hideBuildBlock} editMode={editMode} />;
    case 'search':
      return <CreativeSearcher lineItemType={lineItemType} />;
    default:
      return (
        <CreativeBuilderRenderer
          creativeData={creativeData}
          loadingCreativeData={loadingCreativeData}
          adType={type}
          lineItemType={lineItemType}
          lineItemCountry={lineItemCountry}
          lineItemPublishers={lineItemPublishers}
          isExistingAd={isExistingAd}
          editMode={editMode}
          isAdApproved={isAdApproved}
        />
      );
  }
};

interface CreativeProps {
  lineItemType: AdsPageLineItemType;
  editMode: CreativeEditMode;
  isAdApproved?: boolean;
}

const Creative = ({ lineItemType, editMode, isAdApproved }: CreativeProps): React.JSX.Element => {
  const { isCreativeChanged } = useTrackFormFieldChanges();
  const {
    setFieldValue,
    values: { id: adId, type, creative, creativeChanged, lineItem },
  } = useFormikContext<AdFormValues>();

  useEffect(() => {
    if (creativeChanged !== isCreativeChanged) {
      setFieldValue(AdFormFields.CreativeChanged, isCreativeChanged);
    }
  }, [creativeChanged, isCreativeChanged, setFieldValue]);
  const flags = useFeatureFlags();

  const { mode, id, type: creativeType, url, adSourceId } = creative;
  const { data: creativeData, loading: loadingCreativeData } = useCreativeId({ creativeId: creative?.id || id || '' });
  // override edit mode if creativeBuilder is not enabled
  const isEditMode = !flags?.creativeBuilderEnabled ? CreativeEditMode.readonly : editMode;

  // VastLike includes Vast, Drax, Magnite, etc.
  const isVastLike = url && !adSourceId;

  useEffect(() => {
    // Creatives that do not have vast-style attributes can retain their existing type
    if (!isVastLike) return;

    // If the parent line item is DRAX, then we'll set the creative's type to DRAX by default
    if (lineItemType === 'DRAX' && creative.type !== 'DRAX') setFieldValue('creative', { ...creative, type: 'DRAX' });

    // If the creative has a Vast-Like structure but its type is not Vast, we'll set it to Vast by default
    if (!typeIsVast(creativeType)) setFieldValue('creative', { ...creative, type: 'VAST_VIDEO' });
  }, [creative, creativeType, lineItemType, isVastLike, setFieldValue]);

  return (
    <div className={block()}>
      {cardContentsByMode({
        mode,
        creativeData,
        type,
        lineItemType,
        editMode: isEditMode,
        isExistingAd: !!adId,
        isAdApproved,
        loadingCreativeData,
        lineItemCountry: lineItem?.country,
        lineItemPublishers: lineItem?.displayPublisherTarget || [],
      })}
    </div>
  );
};

export default Creative;
